<template>
    <div>
        <div class="mb-md">
            <h6 class="a-card-title">Investment Breakdown</h6>
            <p class="a-card-description mt-sm">
                View all investments in {{ entityStore.entity.name }}'s technology space.
            </p>
        </div>
        <div class="display-flex mb-lg">
            <AonButton
                class="table-button"
                :class="{ 'active-table-button': activeSideBarType === 'filters' }"
                label="Filters"
                text-with-icon
                type="subtle"
                :icon-options="{ iconStyle: 'fas', iconName: 'fa-bars-filter' }"
                @clicked="() => toggleSideBar('filters', gridApi)"
            />
            <AonButton
                class="ml-md table-button"
                :class="{ 'active-table-button': activeSideBarType === 'columns' }"
                label="Columns"
                text-with-icon
                type="subtle"
                :icon-options="{ iconStyle: 'fas', iconName: 'fa-objects-column' }"
                @clicked="() => toggleSideBar('columns', gridApi)"
            />
            <div
                class="clear-filters display-flex align-items-center cursor-pointer p-sm ml-sm"
                @click="clearFilters"
            >
                <font-awesome-icon :icon="`fas fa-xmark`" class="text-moat-600" />
                <p class="geist-font text-moat-600 bold ml-xs">Clear Filters</p>
            </div>
        </div>
        <MoatTable
            style="width: 100%; height: 500px"
            :class="themeClass"
            :column-defs="colDefs"
            :suppress-auto-size="true"
            :row-data="props.investmentData"
            :tooltip-interaction="true"
            :tooltip-mouse-track="true"
            :tooltip-show-delay="500"
            tooltip-show-mode="whenTruncated"
            :side-bar="sideBar"
            :is-external-filter-present="shouldApplyQuickFilter"
            :does-external-filter-pass="applyQuickFilter"
            @filter-changed="onFilterSortChanged"
            @sort-changed="onFilterSortChanged"
            @grid-ready="gridInit"
        />
    </div>
</template>

<script setup>
import { onMounted, ref, inject, nextTick, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { AgGridVue as MoatTable } from '@ag-grid-community/vue3'
import { useEntityStore } from '@/stores'
import { useMoat2ProductStore } from '@/stores'
import { getInvestmentGroup } from '@/lib/investments'
import { applyQuickFilter as benchmarkingFilter } from '@/lib/benchmarking'
import { useAgGridSidebar } from '@/composables/useAgGridSidebar.js'

const entityStore = useEntityStore()
const moat2ProductStore = useMoat2ProductStore()
const logger = inject('logger')
const filters = inject('filters')
const { t } = useI18n()
const props = defineProps({
    investmentData: {
        type: Array,
        default: [],
    },
    quickFilter: {
        type: String,
        default: 'all',
    },
    defaultInvestmentTypes: {
        type: Array,
        default: () => ['venture_capital', 'private_equity', 'ipo', 'm_and_a', 'other'],
    },
})
const emit = defineEmits(['filter-change'])

const loadingData = ref(true)
const tableData = ref([])
const gridApi = ref(null)
const themeClass = ref('ag-theme-quartz')
const colDefs = ref([
    {
        headerComponent: 'MTCustomColHeader',
        headerComponentParams: {
            headerName: 'Company Name',
            showFilterButton: true,
        },
        cellRenderer: 'MTCustomCellCompany',
        cellRendererParams: {
            allowNewTab: true,
            tabOnLeft: true,
        },
        field: 'entityName',
        headerName: 'Company Name',
        headerTooltip: 'Company Name',
        minWidth: 300,
        sortingOrder: ['asc', 'desc', null],
        filter: 'agTextColumnFilter',
        chartDataType: 'category',
    },
    {
        field: 'productList',
        flex: 2,
        minWidth: 300,
        headerName: 'Products',
        valueGetter: (params) => {
            return params.data.productList ? params.data.productList.split('|') : '-'
        },
        filter: 'agSetColumnFilter',
    },
    {
        field: 'investmentType',
        flex: 1,
        minWidth: 250,
        headerName: 'Investment Type',
        tooltipField: 'investmentType',
        valueFormatter: (params) => {
            return t(`investmentTypes.${params.value}`)
        },
        filter: 'agSetColumnFilter',
        filterParams: {
            values: ['venture_capital', 'private_equity', 'ipo', 'm_and_a', 'other'],
            valueFormatter: (params) => {
                return t(`investmentTypes.${params.value}`)
            },
        },
    },
    {
        field: 'investmentAmount',
        flex: 1,
        minWidth: 250,
        headerName: 'Investment Amount',
        valueFormatter: (params) => {
            return params.value ? `$${params.value.toLocaleString()}` : '-'
        },
        filter: 'agNumberColumnFilter',
    },
    {
        field: 'investmentDate',
        flex: 1,
        minWidth: 250,
        headerName: 'Investment Date',
        valueFormatter: (p) => {
            if (p) {
                return filters.toUTCString(p.value)
            }
        },
        filter: 'agDateColumnFilter',
        sort: 'desc',
    },
    {
        field: 'investors',
        flex: 2,
        minWidth: 300,
        headerName: 'Investors',
        valueGetter: (params) => {
            return params.data.investors ? params.data.investors.split('|') : '-'
        },
        filter: 'agSetColumnFilter',
    },
    {
        field: 'hqCountryName',
        headerName: 'HQ Country',
        hide: true,
        filter: 'agSetColumnFilter',
        chartDataType: 'excluded',
    },
    {
        field: 'totalRevenue',
        headerName: 'Revenue',
        hide: true,
        filter: 'agNumberColumnFilter',
        filterParams: {
            filterOptions: [
                'equals',
                'notEqual',
                'lessThan',
                'lessThanOrEqual',
                'greaterThan',
                'greaterThanOrEqual',
                'inRange',
            ],
            defaultOption: 'greaterThan',
        },
        chartDataType: 'excluded',
        valueFormatter: (p) => {
            if (p.data) {
                return filters.abbreviate(p.data.totalRevenue)
            }
        },
    },
    {
        field: 'employeeCount',
        headerName: 'Employee Count',
        hide: true,
        filter: 'agSetColumnFilter',
        chartDataType: 'excluded',
    },
    {
        field: 'priceToEarnings',
        headerName: 'Price to Earnings',
        hide: true,
        filter: 'agNumberColumnFilter',
        chartDataType: 'excluded',
    },
    {
        field: 'yoyRevenueGrowthPct',
        headerName: 'YoY Revenue Growth %',
        hide: true,
        filter: 'agNumberColumnFilter',
    },
    {
        field: 'marketValue',
        headerName: 'Market Cap',
        hide: true,
        filter: 'agNumberColumnFilter',
    },
    {
        field: 'currency',
        headerName: 'Currency',
        hide: true,
        filter: 'agSetColumnFilter',
    },
    {
        field: 'lastFundingRoundInvestmentType',
        headerName: 'Latest Funding Round',
        hide: true,
        valueGetter: (p) => {
            if (p.data) {
                return getInvestmentGroup(p.data.lastFundingRoundInvestmentType)
            }
        },
        filter: 'agSetColumnFilter',
    },
])

const {
    sideBar,
    activeSideBarType,
    toggleSideBar,
    injectSidebarHeaders,
    clearFilters: sidebarClearFilters,
} = useAgGridSidebar({
    panels: [
        {
            id: 'columns',
            label: 'Columns',
            width: 275,
            toolPanel: 'agColumnsToolPanel',
            toolPanelParams: {
                suppressRowGroups: true,
                suppressValues: true,
                suppressPivotMode: true,
            },
        },
        {
            id: 'filters',
            label: 'Filters',
            width: 275,
            toolPanel: 'agFiltersToolPanel',
        },
    ],
    injectHeaders: {
        filters: 'Filters',
        columns: 'Columns',
    },
    position: 'left',
})

const gridInit = async (params) => {
    gridApi.value = params.api
    injectSidebarHeaders()
    await syncInitialFilters()
    await syncTechAreaFilters()
    params.api.hideOverlay()

    const filtersToolPanel = gridApi.value.getToolPanelInstance('filters')
    filtersToolPanel.expandFilters(['investmentType'])

    gridApi.value.openToolPanel('filters')
}

watch(
    () => props.investmentData,
    (newVal) => {
        if (newVal.length > 0) {
            loadingData.value = false
        }
    },
    { immediate: true }
)

const onFilterSortChanged = async () => {
    const filteredRows = []
    if (!gridApi.value) return

    gridApi.value.forEachNodeAfterFilterAndSort((node) => filteredRows.push(node.data))
    emit('filter-change', filteredRows)
}

// Quick filter
const shouldApplyQuickFilter = () => {
    return props.quickFilter !== 'all'
}

const applyQuickFilter = (node) => {
    return benchmarkingFilter(node.data, props.quickFilter)
}

watch(
    () => props.quickFilter,
    (newVal, oldVal) => {
        if (newVal !== oldVal && gridApi.value) {
            gridApi.value.onFilterChanged()
            //setTimeout(updateRowCount, 0)
        }
    },
    { immediate: false }
)

const getProductNames = (selectedTechAreas) => {
    if (!selectedTechAreas || selectedTechAreas.size === 0) return []

    const productNames = new Set()
    selectedTechAreas.forEach((nodeRef) => {
        if ((nodeRef && nodeRef.parent) || nodeRef.parentNodeId) {
            const parentId = nodeRef.parent || nodeRef.parentNodeId
            const product = moat2ProductStore.allChartData.find((item) => item.nodeId === parentId)
            if (product && product.displayName) {
                productNames.add(product.displayName)
            }
        }
    })

    return Array.from(productNames)
}

const syncInitialFilters = async () => {
    if (gridApi.value) {
        const filterInstance = {
            filterType: 'set',
            values: props.defaultInvestmentTypes,
        }

        await gridApi.value.setColumnFilterModel('investmentType', filterInstance)
        gridApi.value.onFilterChanged()
    }
}

const syncTechAreaFilters = async () => {
    if (gridApi.value) {
        const filterInstance = {
            filterType: 'set',
            values: getProductNames(moat2ProductStore.productAlignmentTechAreaSelections),
        }

        await gridApi.value.setColumnFilterModel('productList', filterInstance)
        gridApi.value.onFilterChanged()
    }
}

watch(
    () => moat2ProductStore.productAlignmentTechAreaSelections,
    (newVal, oldVal) => {
        syncTechAreaFilters(newVal)
    },
    { immediate: true, deep: true }
)

const clearFilters = () => {
    if (gridApi.value) {
        sidebarClearFilters(gridApi.value)
    }
}
</script>
